// client/src/pages/SellerInventory.js
import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { AuthContext } from '../context/AuthContext';
import {
  Box, Typography, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, TextField,
  IconButton, Chip, Alert, CircularProgress, Grid, Card, CardContent, Stack, Button,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { API_BASE_URL, getProductImageUrl } from '../utils/media';
import { LOW_STOCK_THRESHOLD } from '../constants/inventory';
import EmptyState from '../components/EmptyState';

const StatCard = ({ label, value, color }) => (
  <Card variant="outlined">
    <CardContent>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h4" fontWeight={700} color={color}>{value}</Typography>
    </CardContent>
  </Card>
);

const StockCell = ({ quantity, onSave }) => {
  const [value, setValue] = useState(quantity);
  const [saving, setSaving] = useState(false);
  const dirty = Number(value) !== Number(quantity);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(Number(value));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        type="number" size="small" value={value}
        onChange={(e) => setValue(e.target.value)}
        inputProps={{ min: 0 }}
        sx={{ width: 90 }}
      />
      {dirty && (
        <IconButton size="small" color="primary" onClick={handleSave} disabled={saving} aria-label="Save stock">
          <SaveIcon fontSize="small" />
        </IconButton>
      )}
    </Stack>
  );
};

// Adds to the current count, for booking in a delivery.
const AddStock = ({ name, onAdd }) => {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const amount = Number(value);
  const valid = value !== '' && Number.isInteger(amount) && amount !== 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    try {
      await onAdd(amount);
      setValue('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack component="form" onSubmit={handleSubmit} direction="row" spacing={0.5} alignItems="center">
      <TextField
        type="number" size="small" value={value} placeholder="0"
        onChange={(e) => setValue(e.target.value)}
        inputProps={{ step: 1, 'aria-label': `Amount of ${name} to add` }}
        sx={{ width: 80 }}
      />
      <Button type="submit" size="small" variant="outlined" disabled={!valid || saving} sx={{ px: 1.5, minWidth: 0 }}>Add</Button>
    </Stack>
  );
};

const stockChip = (quantity) => {
  const q = Number(quantity);
  if (q <= 0) return <Chip label="Out of Stock" size="small" color="default" />;
  if (q <= LOW_STOCK_THRESHOLD) return <Chip label="Low Stock" size="small" color="warning" />;
  return <Chip label="In Stock" size="small" color="success" />;
};

const SellerInventory = () => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const { token } = useContext(AuthContext);

  const fetchInventory = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/seller/inventory`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch inventory.');
      setInventory(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchInventory(); }, [fetchInventory]);

  const rows = useMemo(() => {
    const out = [];
    inventory.forEach((product) => {
      if (product.variants.length > 0) {
        product.variants.forEach((v) => {
          out.push({
            key: `v${v.id}`, productId: product.id, variantId: v.id,
            name: product.name, imageUrl: product.imageUrl,
            variantLabel: [v.color, v.size].filter(Boolean).join(' / '),
            quantity: v.quantity,
          });
        });
      } else {
        out.push({
          key: `p${product.id}`, productId: product.id, variantId: null,
          name: product.name, imageUrl: product.imageUrl, variantLabel: null,
          quantity: product.base_quantity ?? 0,
        });
      }
    });
    return out;
  }, [inventory]);

  const stats = useMemo(() => ({
    total: rows.length,
    low: rows.filter((r) => Number(r.quantity) > 0 && Number(r.quantity) <= LOW_STOCK_THRESHOLD).length,
    out: rows.filter((r) => Number(r.quantity) <= 0).length,
  }), [rows]);

  const handleAddStock = async (row, adjust) => {
    const url = row.variantId
      ? `${API_BASE_URL}/api/seller/variants/${row.variantId}/stock`
      : `${API_BASE_URL}/api/seller/products/${row.productId}/stock`;
    const response = await fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ adjust }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      alert(data.error || 'Failed to add stock.');
      return;
    }
    await fetchInventory();
  };

  const handleSaveStock = async (row, newQuantity) => {
    const url = row.variantId
      ? `${API_BASE_URL}/api/seller/variants/${row.variantId}/stock`
      : `${API_BASE_URL}/api/seller/products/${row.productId}/stock`;
    const response = await fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ quantity: newQuantity }),
    });
    if (!response.ok) {
      alert('Failed to update stock.');
      return;
    }
    await fetchInventory();
  };

  const term = search.trim().toLowerCase();
  const shownRows = term ? rows.filter((r) => `${r.name} ${r.variantLabel || ''}`.toLowerCase().includes(term)) : rows;

  if (loading) return <CircularProgress size={28} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>Stock</Typography>
          <Typography variant="body2" color="text.secondary">
            Shared by all admins. Use Add to book in a delivery, or type over the count after a stock take.
          </Typography>
        </Box>
        <TextField size="small" placeholder="Search products" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 240 }} />
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
        <Grid item xs={4}><StatCard label="Total SKUs" value={stats.total} /></Grid>
        <Grid item xs={4}><StatCard label="Low Stock" value={stats.low} color="warning.main" /></Grid>
        <Grid item xs={4}><StatCard label="Out of Stock" value={stats.out} color="error.main" /></Grid>
      </Grid>

      {rows.length === 0 ? (
        <EmptyState icon={<Inventory2OutlinedIcon />} title="No inventory yet" description="Products you list will show up here for stock management." />
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell><TableCell>Option</TableCell><TableCell>In stock</TableCell><TableCell>Add stock</TableCell><TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shownRows.map((row) => (
                <TableRow key={row.key} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box component="img" src={getProductImageUrl(row.imageUrl)} alt={row.name} sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1 }} />
                      <Typography variant="body2">{row.name}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{row.variantLabel || '—'}</TableCell>
                  <TableCell>
                    <StockCell key={row.quantity} quantity={row.quantity} onSave={(qty) => handleSaveStock(row, qty)} />
                  </TableCell>
                  <TableCell>
                    <AddStock name={row.name} onAdd={(amount) => handleAddStock(row, amount)} />
                  </TableCell>
                  <TableCell>{stockChip(row.quantity)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

    </Box>
  );
};

export default SellerInventory;
